import { useEffect } from 'react';
import { PortalApiError } from '@/portal/api/client';
import { refreshToken } from '@/portal/api/portalApi';
import { usePortalSession } from '@/portal/stores/portalSessionStore';
import { secondsUntilExpiry } from '@/portal/utils/jwt';

/** The token is renewed when less than this is left of its life (it lasts 60 minutes). */
export const RENEW_WHEN_SECONDS_LEFT = 20 * 60;

const CHECK_EVERY_MS = 60 * 1000;

export const shouldRenew = (secondsLeft: number | null): boolean =>
  secondsLeft !== null && secondsLeft < RENEW_WHEN_SECONDS_LEFT;

/**
 * Keeps an active session signed in: while the page is open the token is swapped for a fresh one before it expires.
 * A 401 from the server (revoked, or older than the maximum session) ends the session; a network error just tries again later.
 */
export const useTokenRefresh = (): void => {
  const token = usePortalSession((state) => state.token);

  useEffect(() => {
    if (!token) return undefined;
    let stopped = false;
    let renewing = false;

    const check = async () => {
      const current = usePortalSession.getState().token;
      if (stopped || renewing || !current || !shouldRenew(secondsUntilExpiry(current))) return;
      renewing = true;
      try {
        const fresh = await refreshToken(current);
        if (!stopped) usePortalSession.getState().renewToken(fresh);
      } catch (error) {
        if (error instanceof PortalApiError && error.status === 401) usePortalSession.getState().endSession();
      } finally {
        renewing = false;
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token]);
};
